import { MakeLogicType, actions, connect, events, kea, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import posthog from 'posthog-js'

import { lemonToast } from '@posthog/lemon-ui'

import api, { ApiConfig } from 'lib/api'
import { SetupTaskId, globalSetupLogic } from 'lib/components/ProductSetup'
import { databaseTableListLogic } from 'scenes/data-management/database/databaseTableListLogic'
import { userLogic } from 'scenes/userLogic'

import { DataModelingSyncInterval, DataWarehouseSavedQuery, DataWarehouseSavedQueryFolder } from '~/types'

import { warehouseSavedQueriesMaterializeCreate } from 'products/data_warehouse/frontend/generated/api'

import type { DatabaseSchemaViewTable } from '../../../queries/schema/schema-general'
import type { UserType } from '../../../types'

// Materialization is async: enabling it flips `is_materialized` either synchronously (the
// /materialize action) or later, when the Temporal job runs (scheduled sync frequency). After
// kicking it off we reload the saved-query list a few times so the sidebar reflects the settled
// state without a manual refresh, then stop.
const MATERIALIZATION_POLL_INTERVAL_MS = 5000
const MATERIALIZATION_POLL_MAX_ATTEMPTS = 12

/** Mirrors the server's default for the materialize action, so the picker opens on what it would pick. */
export const DEFAULT_MATERIALIZE_SYNC_FREQUENCY: DataModelingSyncInterval = '24hour'

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataWarehouseViewsLogicValues {
    databaseLoading: boolean // databaseTableListLogic
    views: DatabaseSchemaViewTable[] // databaseTableListLogic
    user: UserType | null // userLogic
    dataWarehouseSavedQueries: DataWarehouseSavedQuery[]
    dataWarehouseSavedQueriesLoading: boolean
    dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[]
    dataWarehouseSavedQueryFoldersById: Record<string, DataWarehouseSavedQueryFolder>
    dataWarehouseSavedQueryFoldersLoading: boolean
    dataWarehouseSavedQueryMap: Record<string, DataWarehouseSavedQuery>
    dataWarehouseSavedQueryMapById: Record<string, DataWarehouseSavedQuery>
    dataWarehouseSavedQueryMapByIdStringMap: Record<string, DataWarehouseSavedQuery>
    initialDataWarehouseSavedQueryLoading: boolean
    materializingViewIds: string[]
    shouldShowEmptyState: boolean
    updatingDataWarehouseSavedQuery: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataWarehouseViewsLogicActions {
    refreshDatabaseSchema: () => {
        value: true
    } // databaseTableListLogic
    addMaterializingViews: (viewIds: string[]) => {
        viewIds: string[]
    }
    cancelDataWarehouseSavedQuery: (viewId: string) => {
        viewId: string
    }
    clearMaterializingView: (viewId: string) => {
        viewId: string
    }
    createDataWarehouseSavedQuery: (
        view: Partial<DataWarehouseSavedQuery> & {
            dag_id?: string
            folder_id?: string | null
            types: string[][]
        }
    ) => Partial<DataWarehouseSavedQuery> & {
        types: string[][]
        folder_id?: string | null
        dag_id?: string
    }
    createDataWarehouseSavedQueryFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    createDataWarehouseSavedQueryFolder: (name: string) => string
    createDataWarehouseSavedQueryFolderFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    createDataWarehouseSavedQueryFolderSuccess: (
        dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[],
        payload?: string
    ) => {
        dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[]
        payload?: string
    }
    createDataWarehouseSavedQuerySuccess: (
        dataWarehouseSavedQueries: DataWarehouseSavedQuery[],
        payload?: Partial<DataWarehouseSavedQuery> & {
            types: string[][]
            folder_id?: string | null
            dag_id?: string
        }
    ) => {
        dataWarehouseSavedQueries: DataWarehouseSavedQuery[]
        payload?: Partial<DataWarehouseSavedQuery> & {
            types: string[][]
            folder_id?: string | null
            dag_id?: string
        }
    }
    deleteDataWarehouseSavedQuery: (viewId: string) => string
    deleteDataWarehouseSavedQueryFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteDataWarehouseSavedQueryFolder: (folderId: string) => string
    deleteDataWarehouseSavedQueryFolderFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deleteDataWarehouseSavedQueryFolderSuccess: (
        dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[],
        payload?: string
    ) => {
        dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[]
        payload?: string
    }
    deleteDataWarehouseSavedQuerySuccess: (
        dataWarehouseSavedQueries: DataWarehouseSavedQuery[],
        payload?: string
    ) => {
        dataWarehouseSavedQueries: DataWarehouseSavedQuery[]
        payload?: string
    }
    loadDataWarehouseSavedQueries: () => any
    loadDataWarehouseSavedQueriesFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadDataWarehouseSavedQueriesSuccess: (
        dataWarehouseSavedQueries: DataWarehouseSavedQuery[],
        payload?: any
    ) => {
        dataWarehouseSavedQueries: DataWarehouseSavedQuery[]
        payload?: any
    }
    loadDataWarehouseSavedQueryFolders: () => any
    loadDataWarehouseSavedQueryFoldersFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadDataWarehouseSavedQueryFoldersSuccess: (
        dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[],
        payload?: any
    ) => {
        dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[]
        payload?: any
    }
    materializeDataWarehouseSavedQuery: (
        viewId: string,
        syncFrequency?: DataModelingSyncInterval
    ) => {
        syncFrequency: DataModelingSyncInterval | undefined
        viewId: string
    }
    revertMaterialization: (viewId: string) => {
        viewId: string
    }
    runDataWarehouseSavedQuery: (viewId: string) => {
        viewId: string
    }
    updateDataWarehouseSavedQuery: (
        view: Partial<DataWarehouseSavedQuery> & {
            edited_history_id?: string
            folder_id?: string | null
            id: string
            lifecycle?: string
            shouldRematerialize?: boolean
            soft_update?: boolean
            sync_frequency?: string
            types?: string[][]
        }
    ) => Partial<DataWarehouseSavedQuery> & {
        id: string
        types?: string[][]
        sync_frequency?: string
        lifecycle?: string
        shouldRematerialize?: boolean
        edited_history_id?: string
        folder_id?: string | null
        soft_update?: boolean
    }
    updateDataWarehouseSavedQueryFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateDataWarehouseSavedQueryFolder: ({ id, name }: { id: string; name: string }) => {
        id: string
        name: string
    }
    updateDataWarehouseSavedQueryFolderFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updateDataWarehouseSavedQueryFolderSuccess: (
        dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[],
        payload?: {
            id: string
            name: string
        }
    ) => {
        dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[]
        payload?: {
            id: string
            name: string
        }
    }
    updateDataWarehouseSavedQuerySuccess: (
        dataWarehouseSavedQueries: DataWarehouseSavedQuery[],
        payload?: Partial<DataWarehouseSavedQuery> & {
            id: string
            types?: string[][]
            sync_frequency?: string
            lifecycle?: string
            shouldRematerialize?: boolean
            edited_history_id?: string
            folder_id?: string | null
            soft_update?: boolean
        }
    ) => {
        dataWarehouseSavedQueries: DataWarehouseSavedQuery[]
        payload?: Partial<DataWarehouseSavedQuery> & {
            id: string
            types?: string[][]
            sync_frequency?: string
            lifecycle?: string
            shouldRematerialize?: boolean
            edited_history_id?: string
            folder_id?: string | null
            soft_update?: boolean
        }
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface dataWarehouseViewsLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        shouldShowEmptyState: (views: DatabaseSchemaViewTable[], databaseLoading: boolean) => boolean
        dataWarehouseSavedQueryMapById: (
            dataWarehouseSavedQueries: DataWarehouseSavedQuery[]
        ) => Record<string, DataWarehouseSavedQuery>
        dataWarehouseSavedQueryMapByIdStringMap: (
            dataWarehouseSavedQueries: DataWarehouseSavedQuery[]
        ) => Record<string, DataWarehouseSavedQuery>
        dataWarehouseSavedQueryMap: (
            dataWarehouseSavedQueries: DataWarehouseSavedQuery[]
        ) => Record<string, DataWarehouseSavedQuery>
        dataWarehouseSavedQueryFoldersById: (
            dataWarehouseSavedQueryFolders: DataWarehouseSavedQueryFolder[]
        ) => Record<string, DataWarehouseSavedQueryFolder>
    }
}

export type dataWarehouseViewsLogicType = MakeLogicType<
    dataWarehouseViewsLogicValues,
    dataWarehouseViewsLogicActions,
    Record<string, any>,
    dataWarehouseViewsLogicMeta
>

export const dataWarehouseViewsLogic = kea<dataWarehouseViewsLogicType>([
    path(['scenes', 'warehouse', 'dataWarehouseSavedQueriesLogic']),
    connect(() => ({
        values: [userLogic, ['user'], databaseTableListLogic, ['views', 'databaseLoading']],
        actions: [databaseTableListLogic, ['refreshDatabaseSchema']],
    })),
    reducers({
        initialDataWarehouseSavedQueryLoading: [
            true,
            {
                loadDataWarehouseSavedQueriesSuccess: () => false,
                loadDataWarehouseSavedQueriesFailure: () => false,
            },
        ],
        updatingDataWarehouseSavedQuery: [
            false,
            {
                updateDataWarehouseSavedQuery: () => true,
                updateDataWarehouseSavedQuerySuccess: () => false,
                updateDataWarehouseSavedQueryFailure: () => false,
            },
        ],
        // Views whose materialization is in flight. Reactive so the sidebar can show a spinner in
        // place of the view icon while it materializes (a stable state, unlike the transient list
        // loading flag), then flip to the materialized icon once it settles.
        materializingViewIds: [
            [] as string[],
            {
                addMaterializingViews: (state, { viewIds }) => Array.from(new Set([...state, ...viewIds])),
                clearMaterializingView: (state, { viewId }) => state.filter((id) => id !== viewId),
            },
        ],
    }),
    actions({
        runDataWarehouseSavedQuery: (viewId: string) => ({ viewId }),
        cancelDataWarehouseSavedQuery: (viewId: string) => ({ viewId }),
        materializeDataWarehouseSavedQuery: (viewId: string, syncFrequency?: DataModelingSyncInterval) => ({
            viewId,
            syncFrequency,
        }),
        revertMaterialization: (viewId: string) => ({ viewId }),
        addMaterializingViews: (viewIds: string[]) => ({ viewIds }),
        clearMaterializingView: (viewId: string) => ({ viewId }),
    }),
    loaders(({ values }) => ({
        dataWarehouseSavedQueries: [
            [] as DataWarehouseSavedQuery[],
            {
                loadDataWarehouseSavedQueries: async () => {
                    const savedQueries = await api.dataWarehouseSavedQueries.list()
                    return savedQueries.results
                },
                createDataWarehouseSavedQuery: async (
                    view: Partial<DataWarehouseSavedQuery> & {
                        types: string[][]
                        folder_id?: string | null
                        dag_id?: string
                    }
                ) => {
                    const newView = await api.dataWarehouseSavedQueries.create(view)

                    lemonToast.success(`${newView.name ?? 'View'} successfully created`)
                    globalSetupLogic.findMounted()?.actions.markTaskAsCompleted(SetupTaskId.CreateSavedView)

                    return [...values.dataWarehouseSavedQueries, newView]
                },
                deleteDataWarehouseSavedQuery: async (viewId: string) => {
                    await api.dataWarehouseSavedQueries.delete(viewId)
                    return values.dataWarehouseSavedQueries.filter((view) => view.id !== viewId)
                },
                updateDataWarehouseSavedQuery: async (
                    view: Partial<DataWarehouseSavedQuery> & {
                        id: string
                        types?: string[][]
                        sync_frequency?: string
                        lifecycle?: string
                        shouldRematerialize?: boolean
                        edited_history_id?: string
                        folder_id?: string | null
                        soft_update?: boolean
                    }
                ) => {
                    const newView = await api.dataWarehouseSavedQueries.update(view.id, view)
                    return values.dataWarehouseSavedQueries.map((savedQuery) => {
                        if (savedQuery.id === view.id) {
                            return newView
                        }
                        return savedQuery
                    })
                },
            },
        ],
        dataWarehouseSavedQueryFolders: [
            [] as DataWarehouseSavedQueryFolder[],
            {
                loadDataWarehouseSavedQueryFolders: async () => {
                    return await api.dataWarehouseSavedQueryFolders.list()
                },
                createDataWarehouseSavedQueryFolder: async (name: string) => {
                    const folder = await api.dataWarehouseSavedQueryFolders.create({ name })
                    lemonToast.success('Folder created')
                    return [...values.dataWarehouseSavedQueryFolders, folder].sort((a, b) =>
                        a.name.localeCompare(b.name)
                    )
                },
                updateDataWarehouseSavedQueryFolder: async ({ id, name }: { id: string; name: string }) => {
                    const updatedFolder = await api.dataWarehouseSavedQueryFolders.update(id, { name })
                    return values.dataWarehouseSavedQueryFolders
                        .map((folder) => (folder.id === id ? updatedFolder : folder))
                        .sort((a, b) => a.name.localeCompare(b.name))
                },
                deleteDataWarehouseSavedQueryFolder: async (folderId: string) => {
                    await api.dataWarehouseSavedQueryFolders.delete(folderId)
                    return values.dataWarehouseSavedQueryFolders.filter((folder) => folder.id !== folderId)
                },
            },
        ],
    })),
    listeners(({ actions, values, cache }) => ({
        loadDataWarehouseSavedQueriesSuccess: ({ dataWarehouseSavedQueries }) => {
            const materializing = values.materializingViewIds
            if (materializing.length === 0) {
                return
            }
            const settledIds = new Set(
                dataWarehouseSavedQueries.filter((view) => view.id && view.is_materialized).map((view) => view.id)
            )
            // Per-view attempt counts so concurrent materializations don't share a budget — one view
            // settling or capping out never clears another view's spinner early.
            const attempts: Record<string, number> = cache.materializationPollAttempts ?? {}
            const stillPending: string[] = []
            for (const id of materializing) {
                if (settledIds.has(id)) {
                    // Settled → spinner flips to the materialized icon.
                    actions.clearMaterializingView(id)
                    delete attempts[id]
                    continue
                }
                const nextAttempt = (attempts[id] ?? 0) + 1
                if (nextAttempt >= MATERIALIZATION_POLL_MAX_ATTEMPTS) {
                    // Give up waiting on this view so its spinner doesn't get stuck forever.
                    actions.clearMaterializingView(id)
                    delete attempts[id]
                } else {
                    attempts[id] = nextAttempt
                    stillPending.push(id)
                }
            }
            cache.materializationPollAttempts = attempts
            if (stillPending.length === 0) {
                cache.disposables.dispose('materializationPoll')
                return
            }
            cache.disposables.add(() => {
                const timeoutId = setTimeout(
                    () => actions.loadDataWarehouseSavedQueries(),
                    MATERIALIZATION_POLL_INTERVAL_MS
                )
                return () => clearTimeout(timeoutId)
            }, 'materializationPoll')
        },
        createDataWarehouseSavedQuerySuccess: () => {
            // The create loader already appends the new view optimistically, so the row appears
            // without an extra reload. A reload here would also race the materialize reload below
            // (create-with-materialize) and could revert is_materialized back to false.
            actions.refreshDatabaseSchema()
        },
        createDataWarehouseSavedQueryFolderSuccess: () => {
            actions.loadDataWarehouseSavedQueries()
        },
        updateDataWarehouseSavedQueryFolderSuccess: () => {
            lemonToast.success('Folder renamed')
        },
        updateDataWarehouseSavedQueryFolderFailure: () => {
            lemonToast.error('Failed to rename folder')
        },
        updateDataWarehouseSavedQuerySuccess: ({ payload }) => {
            // in the case where we are scheduling a materialized view, send an event
            if (payload && payload.lifecycle && payload.sync_frequency) {
                // this function exists as an upsert, so we need to check if the view was created or updated
                posthog.capture(`materialized view ${payload.lifecycle === 'update' ? 'updated' : 'created'}`, {
                    sync_frequency: payload.sync_frequency,
                })
                if (payload.lifecycle === 'update') {
                    lemonToast.success('Sync frequency updated')
                }
            }

            if (payload?.shouldRematerialize) {
                actions.runDataWarehouseSavedQuery(payload.id)
            }

            // Enabling a sync frequency schedules materialization, whose is_materialized flip can
            // land asynchronously — mark it materializing (spinner) and poll until it settles.
            if (payload?.id && payload.sync_frequency) {
                actions.addMaterializingViews([payload.id])
            }

            actions.loadDataWarehouseSavedQueries()
            actions.refreshDatabaseSchema()
        },
        updateDataWarehouseSavedQueryFailure: ({ errorObject }) => {
            lemonToast.error(errorObject?.detail || 'Failed to update view')
            actions.loadDataWarehouseSavedQueries()
        },
        deleteDataWarehouseSavedQuerySuccess: () => {
            lemonToast.success('View deleted')
            // The delete loader already filters the view out of the list, so the sidebar row leaves
            // immediately. Reloading the whole list here just replaces every row's identity and
            // makes the tree flash — skip it and let refreshDatabaseSchema update the picker.
            actions.refreshDatabaseSchema()
        },
        deleteDataWarehouseSavedQueryFolderSuccess: () => {
            lemonToast.success('Folder deleted')
            actions.loadDataWarehouseSavedQueries()
            actions.refreshDatabaseSchema()
        },
        runDataWarehouseSavedQuery: async ({ viewId }) => {
            try {
                await api.dataWarehouseSavedQueries.run(viewId)
                lemonToast.success('Materialization started')
                actions.loadDataWarehouseSavedQueries()
            } catch {
                lemonToast.error(`Failed to run materialization`)
            }
        },
        cancelDataWarehouseSavedQuery: async ({ viewId }) => {
            try {
                await api.dataWarehouseSavedQueries.cancel(viewId)
                lemonToast.success('Materialization cancelled')
                actions.loadDataWarehouseSavedQueries()
            } catch {
                lemonToast.error(`Failed to cancel materialization`)
            }
        },
        materializeDataWarehouseSavedQuery: async ({ viewId, syncFrequency }) => {
            const requestedFrequency = syncFrequency ?? DEFAULT_MATERIALIZE_SYNC_FREQUENCY
            try {
                await warehouseSavedQueriesMaterializeCreate(String(ApiConfig.getCurrentTeamId()), viewId, {
                    sync_frequency: requestedFrequency,
                })
                lemonToast.success('View materialized successfully')
                posthog.capture('materialized view created', {
                    sync_frequency: requestedFrequency,
                })
                actions.addMaterializingViews([viewId])
                actions.loadDataWarehouseSavedQueries()
                actions.refreshDatabaseSchema()
            } catch (error: any) {
                // The server rejects a cadence the query's lineage can't support and says which one
                // to pick instead, so the reason has to reach the user rather than a generic failure.
                lemonToast.error(error?.detail || 'Failed to materialize view')
            }
        },
        revertMaterialization: async ({ viewId }) => {
            try {
                await api.dataWarehouseSavedQueries.revertMaterialization(viewId)
                lemonToast.success('Materialization reverted')
                // No longer materializing — drop it so the spinner stops and we don't wait for a
                // flag that will never flip.
                actions.clearMaterializingView(viewId)
                actions.loadDataWarehouseSavedQueries()
                actions.refreshDatabaseSchema()
            } catch {
                lemonToast.error(`Failed to revert materialization`)
            }
        },
    })),
    selectors({
        shouldShowEmptyState: [
            (s) => [s.views, s.databaseLoading],
            (views: import('../../../queries/schema').DatabaseSchemaViewTable[], databaseLoading: boolean): boolean => {
                return views?.length == 0 && !databaseLoading
            },
        ],
        dataWarehouseSavedQueryMapById: [
            (s) => [s.dataWarehouseSavedQueries],
            (dataWarehouseSavedQueries: DataWarehouseSavedQuery[]) => {
                return (
                    dataWarehouseSavedQueries?.reduce(
                        (acc, cur) => {
                            acc[cur.id] = cur
                            return acc
                        },
                        {} as Record<string, DataWarehouseSavedQuery>
                    ) ?? {}
                )
            },
        ],
        // id hyphens are removed. Used for hex'd id paths in DAG
        dataWarehouseSavedQueryMapByIdStringMap: [
            (s) => [s.dataWarehouseSavedQueries],
            (dataWarehouseSavedQueries: DataWarehouseSavedQuery[]) => {
                return (
                    dataWarehouseSavedQueries?.reduce(
                        (acc, cur) => {
                            acc[cur.id.replace(/-/g, '')] = cur
                            return acc
                        },
                        {} as Record<string, DataWarehouseSavedQuery>
                    ) ?? {}
                )
            },
        ],
        dataWarehouseSavedQueryMap: [
            (s) => [s.dataWarehouseSavedQueries],
            (dataWarehouseSavedQueries: DataWarehouseSavedQuery[]) => {
                return (
                    dataWarehouseSavedQueries?.reduce(
                        (acc, cur) => {
                            acc[cur.name] = cur
                            return acc
                        },
                        {} as Record<string, DataWarehouseSavedQuery>
                    ) ?? {}
                )
            },
        ],
        dataWarehouseSavedQueryFoldersById: [
            (s) => [s.dataWarehouseSavedQueryFolders],
            (folders: DataWarehouseSavedQueryFolder[]) => {
                return folders.reduce(
                    (acc, folder) => {
                        acc[folder.id] = folder
                        return acc
                    },
                    {} as Record<string, DataWarehouseSavedQueryFolder>
                )
            },
        ],
    }),
    events(({ actions }) => ({
        afterMount: () => {
            actions.loadDataWarehouseSavedQueries()
            actions.loadDataWarehouseSavedQueryFolders()
        },
    })),
])
